// ============================================================
// Field.js
// Form field wrapper: label, error message, optional help text.
// Pairs with native <input>/<select>/<textarea> children so all
// validation feedback rendering is consistent across forms.
//
// Usage:
//   <Field label="Email" error={errors.email} required>
//     <input type="email" value={...} onChange={...} />
//   </Field>
// ============================================================

import React, { useId, cloneElement, isValidElement, Children } from 'react';
import './Field.css';

export default function Field({ label, error, help, required, children }) {
  const generatedId = useId();
  const hasError = Boolean(error);

  // Auto-wire id + aria-invalid + aria-describedby onto the first child
  // if it's a single React element. Callers can override by passing
  // these props explicitly.
  const child = Children.count(children) === 1 && isValidElement(children)
    ? cloneElement(children, {
        id: children.props.id || generatedId,
        'aria-invalid': hasError || children.props['aria-invalid'],
        'aria-describedby': hasError ? `${generatedId}-error` : children.props['aria-describedby'],
      })
    : children;

  const inputId = isValidElement(children) ? (children.props.id || generatedId) : generatedId;

  return (
    <div className={`field ${hasError ? 'field--error' : ''}`}>
      {label && (
        <label htmlFor={inputId} className="field__label">
          {label}{required && <span className="field__required" aria-hidden="true"> *</span>}
        </label>
      )}
      {child}
      {help && !hasError && <p className="field__help">{help}</p>}
      {hasError && <p id={`${generatedId}-error`} className="field__error" role="alert">{error}</p>}
    </div>
  );
}
